import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { orgService } from '../services';
import './GerenciasPanel.css';

const GerenciasPanel = () => {
  const { usuario, esPresidente } = useAuth();
  const [gerencias, setGerencias] = useState([]);
  const [vicepresidencias, setVicepresidencias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // Form states
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [vpSeleccionada, setVpSeleccionada] = useState('');
  const [mensajeExito, setMensajeExito] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Filtro para Presidente
  const [filtroVp, setFiltroVp] = useState('');

  const cargarDatos = async () => {
    try {
      setCargando(true);
      setError(null);
      
      let params = {};
      // Si no es presidente, el backend filtra automáticamente las gerencias de su VP,
      // pero si es presidente, podemos filtrar en el frontend o backend por id
      if (esPresidente() && filtroVp) {
        params.vicepresidencia = filtroVp;
      }
      
      const datosGerencias = await orgService.getGerencias(params);
      setGerencias(datosGerencias.results || datosGerencias);

      if (esPresidente()) {
        const datosVps = await orgService.getVicepresidencias();
        setVicepresidencias(datosVps.results || datosVps);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar gerencias');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [filtroVp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) return;

    try {
      setGuardando(true);
      setError(null);
      setMensajeExito('');

      const body = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
      };

      // Si es presidente, debe enviar a qué VP pertenece la gerencia
      if (esPresidente()) {
        if (!vpSeleccionada) {
          setError('Debe seleccionar una Vicepresidencia.');
          setGuardando(false);
          return;
        }
        body.vicepresidencia = vpSeleccionada;
      } else {
        // Para VPs, el backend asociará automáticamente la gerencia a la VP del usuario logueado,
        // pero por seguridad también podemos enviarla si la tenemos en usuario.vicepresidencia
        if (usuario?.vicepresidencia) {
          body.vicepresidencia = usuario.vicepresidencia.id || usuario.vicepresidencia;
        }
      }

      await orgService.createGerencia(body);
      
      setNombre('');
      setDescripcion('');
      setMensajeExito('Gerencia creada exitosamente.');
      setTimeout(() => setMensajeExito(''), 4000);
      
      cargarDatos();
    } catch (err) {
      setError(err.response?.data?.non_field_errors?.[0] || err.response?.data?.detail || 'Error al crear gerencia');
    } finally {
      setGuardando(false);
    }
  };

  const handleToggleActiva = async (gerencia) => {
    try {
      await orgService.updateGerencia(gerencia.id, {
        activa: !gerencia.activa,
      });
      cargarDatos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cambiar estado de la gerencia');
    }
  };

  return (
    <div className="gerencias-panel-container">
      <div className="gerencias-header">
        <div>
          <h1>Gestión de Gerencias</h1>
          <p className="subtitle">
            {esPresidente() 
              ? 'Administre las gerencias asociadas a todas las vicepresidencias.' 
              : `Gerencias asociadas a la ${usuario?.vicepresidencia?.nombre || 'Vicepresidencia'}`}
          </p>
        </div>
      </div>

      {error && <div className="error-banner">⚠️ {error}</div>}
      {mensajeExito && <div className="exito-banner">✅ {mensajeExito}</div>}

      <div className="gerencias-layout">
        {/* Formulario de creación */}
        <div className="card-glass form-section">
          <h2>Nueva Gerencia</h2>
          <form onSubmit={handleSubmit}>
            {esPresidente() && (
              <div className="form-group">
                <label>Vicepresidencia Destino</label>
                <select 
                  value={vpSeleccionada} 
                  onChange={(e) => setVpSeleccionada(e.target.value)}
                  required
                >
                  <option value="">-- Seleccione una Vicepresidencia --</option>
                  {vicepresidencias.map(vp => (
                    <option key={vp.id} value={vp.id}>{vp.nombre} ({vp.abreviatura})</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Nombre de la Gerencia</label>
              <input
                type="text"
                placeholder="Ej: Gerencia de Facturación"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                maxLength="150"
                required
              />
            </div>

            <div className="form-group">
              <label>Descripción</label>
              <textarea
                placeholder="Breve descripción del alcance de esta gerencia..."
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                rows="4"
              />
            </div>

            <button type="submit" className="btn-primary" disabled={guardando}>
              {guardando ? 'Guardando...' : 'Crear Gerencia'}
            </button>
          </form>
        </div>

        {/* Listado y Filtros */}
        <div className="list-section">
          {esPresidente() && (
            <div className="card-glass filter-bar">
              <label>Filtrar por Vicepresidencia:</label>
              <select value={filtroVp} onChange={(e) => setFiltroVp(e.target.value)}>
                <option value="">Todas las Vicepresidencias</option>
                {vicepresidencias.map(vp => (
                  <option key={vp.id} value={vp.id}>{vp.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {cargando ? (
            <div className="loading-state">Cargando gerencias...</div>
          ) : gerencias.length === 0 ? (
            <div className="card-glass empty-state">
              <p>No se encontraron gerencias registradas.</p>
            </div>
          ) : (
            <div className="gerencias-grid">
              {(esPresidente() && filtroVp ? gerencias.filter(g => String(g.vicepresidencia) === String(filtroVp)) : gerencias).map((g) => (
                <div key={g.id} className={`card-glass gerencia-card ${!g.activa ? 'desactivada' : ''}`}>
                  <div className="card-header">
                    <h3>{g.nombre}</h3>
                    <span className={`status-badge ${g.activa ? 'activa' : 'inactiva'}`}>
                      {g.activa ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  
                  {esPresidente() && g.vicepresidencia_nombre && (
                    <div className="vp-tag">
                      🏢 {g.vicepresidencia_nombre}
                    </div>
                  )}

                  <p className="desc">{g.descripcion || 'Sin descripción proporcionada.'}</p>
                  
                  <div className="card-footer">
                    <span className="fecha">Creada: {new Date(g.fecha_creacion).toLocaleDateString()}</span>
                    <button 
                      type="button" 
                      className={`btn-action ${g.activa ? 'btn-disable' : 'btn-enable'}`}
                      onClick={() => handleToggleActiva(g)}
                    >
                      {g.activa ? 'Desactivar' : 'Activar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GerenciasPanel;
